import { useSelector, useDispatch } from "react-redux";

import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { actionAppBarFloorUpdated } from "./AppBarRedux";
import { trackEvent } from "../../tracking";
import { useEffect } from "react";

const formControl_ = (theme) => ({
  minWidth: 90,
  minHeight: 48,
  [theme.breakpoints.down("sm")]: {
    minHeight: 40,
  },
  "& .MuiSelect-select.MuiSelect-select": {
    minHeight: "unset !important",
  },
  "& .MuiFormLabel-root": {
    color: "rgba(0, 0, 0, 0.87)",
  },
  "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  "& .MuiInputBase-root": {
    lineHeight: 0.7,
    [theme.breakpoints.down("sm")]: {
      lineHeight: 0.4,
    },
  },
  "& .MuiOutlinedInput-notchedOutline": {
    border: "none",
  },
});

const label_ = (theme) => ({
  top: -5,
  [theme.breakpoints.down("sm")]: {
    top: -7,
  },
  "&.MuiInputLabel-outlined.MuiInputLabel-shrink": {
    transform: "translate(7px, 3px) scale(0.6)",
  },
});

const menuItem_ = (theme) => ({
  minHeight: "44px !important",
});

export default function Floor() {
  const dispatch = useDispatch();

  const floor = useSelector((state) => state.appBarReducer.floor);
  const renderedFloor = useSelector(
    (state) => state.appBarReducer.renderedFloor,
  );
  const floors = useSelector((state) => state.appBarReducer.floors);
  const floorsInView = useSelector((state) => state.appBarReducer.floorsInView);
  const floorsInCurrentSearch = useSelector(
    (state) => state.appBarReducer.floorsInCurrentSearch,
  );
  const floorsInActiveBuilding = useSelector(
    (state) => state.appBarReducer.floorsInActiveBuilding,
  );
  const highlightFloors = new Set(floorsInCurrentSearch);
  const floorOptions = createFloorOptions(
    floor,
    floors,
    floorsInView,
    floorsInCurrentSearch,
  );

  useEffect(() => {
    if (!floor && renderedFloor) {
      // The user has not selected a floor yet, but some floor is rendering
      // in the map.  Auto-switch to that floor so the user knows which
      // floor they are seeing in the map.
      dispatch(actionAppBarFloorUpdated(renderedFloor));
    }
  }, [floor, renderedFloor]);

  const handleOnOpen = () => {
    trackEvent({
      category: "Map Tools",
      action: "Floor Dropdown",
    });
  };

  const handleChange = (event) => {
    dispatch(actionAppBarFloorUpdated(event.target.value));
  };

  const getBackgroundColor = (floorOption) => {
    const BLUE = "rgba(59, 178, 208, 0.7)";
    const LIGHT_BLUE = "rgba(59, 178, 208, 0.3)";
    const SELECTED = "rgba(0, 0, 0, 0.15)";

    if (highlightFloors.has(floorOption) && floorOption === floor) {
      return BLUE;
    } else if (highlightFloors.has(floorOption)) {
      return LIGHT_BLUE;
    } else if (floorOption === floor) {
      return SELECTED;
    } else {
      return "default";
    }
  };

  const getFontWeight = (floorOption) => {
    return floorsInActiveBuilding.indexOf(floorOption) !== -1
      ? "bolder"
      : "normal";
  };

  const setStyle = (floorOptions) => {
    return {
      backgroundColor: getBackgroundColor(floorOptions),
      fontWeight: getFontWeight(floorOptions),
    };
  };

  const floorLabel = "Floor";
  return (
    <Tooltip
      title="Building Floor"
      // The Tooltip text will appear in an "aria-label" attribute.
      // By adding this role, we create a "Role Region":
      // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/region_role
      // This avoids a "Text not included in an ARIA landmark" complaint in the
      // Siteimprove browser plugin.
      role="region"
      placement="right"
    >
      <FormControl variant="outlined" sx={formControl_}>
        <InputLabel
          htmlFor="floor-dropdown-input"
          id="floor-dropdown-label"
          sx={label_}
        >
          <Typography variant="button" component="span">
            {floorLabel}
          </Typography>
        </InputLabel>
        <Select
          labelId="floor-dropdown-label"
          label={floorLabel}
          id="floor-dropdown"
          inputProps={{ id: "floor-dropdown-input" }}
          value={floor}
          onOpen={handleOnOpen}
          onChange={handleChange}
        >
          {
            /* Once a floor has been selected, remove None from the choices. */
            !floor && (
              <MenuItem sx={menuItem_} value="">
                None
              </MenuItem>
            )
          }
          {floorOptions.map((floor) => {
            return (
              <MenuItem
                sx={menuItem_}
                style={setStyle(floor)}
                value={floor}
                key={floor}
              >
                {floor}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Tooltip>
  );
}

function createFloorOptions(
  floor,
  floors,
  floorsInView,
  floorsInCurrentSearch,
) {
  const floorOptions = [
    ...new Set([floor, ...floors, ...floorsInView, ...floorsInCurrentSearch]),
  ];
  floorOptions.sort(floorSorter);
  return floorOptions.filter((floor) => floor !== "");
}

export function floorSorter(a, b) {
  const scoreA = getFloorScore(a);
  const scoreB = getFloorScore(b);
  return scoreB - scoreA;
}

const SPECIAL_FLOORS = {
  HP: 100001,
  PH: 100000,
};

function getFloorScore(floor) {
  if (floor in SPECIAL_FLOORS) return SPECIAL_FLOORS[floor];

  const nonzero = removeLeadingZero(floor);
  const leading = getFirstDigitOrChar(nonzero);
  const trailing = getFirstDigitOrChar(nonzero.replace(leading, ""));
  return getTotalFloorScore(leading, trailing);
}

function sumAscii(targetString) {
  let asciiVal = 0;
  for (let i = 0; i < targetString.length; i++) {
    asciiVal += targetString.charCodeAt(i);
  }
  return asciiVal;
}

function getScoreForFloors(targetString) {
  const num = parseInt(targetString);
  let score = 0;

  if (targetString && !isNaN(num)) {
    score = sumAscii(targetString);
  } else if (targetString && isNaN(num)) {
    if (targetString === "G") {
      // ascii Z - A, make ground level score between 0 and A
      score = 90 - 65 + 1;
    } else {
      // ascii Z == 90
      score = 90 - sumAscii(targetString);
    }
  }
  return score;
}

function getTotalFloorScore(lead, trail) {
  const score = getScoreForFloors(lead);
  const decimalDigits = getScoreForFloors(trail);
  const decimalPlace = decimalDigits.toString().length;
  const decimal = decimalDigits / Math.pow(10, decimalPlace);
  return score + decimal;
}

function removeLeadingZero(targetString) {
  return targetString.startsWith("0")
    ? targetString.replace("0", "")
    : targetString;
}

function getFirstDigitOrChar(targetString) {
  const num = parseInt(targetString);
  return isNaN(num) ? targetString.charAt(0) : num.toString();
}
